import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields/TextInput";
import type { DegreeDetail, DegreePayload } from "@/interface/masterManagement";
import { useCreateDegree, useUpdateDegree } from "../hooks/useDegree";

const degreeSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Degree Name is required")
    .max(255, "Degree Name cannot exceed 255 characters"),
});

export type DegreeFormValues = z.infer<typeof degreeSchema>;

interface DegreeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: DegreeDetail | null;
}

export const DegreeFormModal: React.FC<DegreeFormModalProps> = ({
  isOpen,
  onClose,
  editData,
}) => {
  const createMutation = useCreateDegree();
  const updateMutation = useUpdateDegree();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<DegreeFormValues>({
    resolver: zodResolver(degreeSchema),
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        reset({
          name: editData.degree_name || editData.name || "",
        });
      } else {
        reset({
          name: "",
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: DegreeFormValues) => {
    try {
      const payload: DegreePayload = {
        degree_name: values.name,
        name: values.name,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Error handled by query/API fallback
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {editData ? "Edit Degree" : "Add Degree"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="name"
              label="Degree / Qualification Title"
              placeholder="e.g. Bachelor of Technology (B.Tech)"
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
            >
              {isSubmitting || createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : editData
                ? "Save Changes"
                : "Add Degree"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DegreeFormModal;
